import type React from 'react';
import { useState } from 'react';
import { useTheme, type ThemeTokens } from '@course-authoring/shared-types';
import { useTranslation } from '@course-authoring/i18n';
import { createContentBlockStyles } from './styles/contentBlocks';
import { useIsMobileViewport } from './hooks/useIsMobileViewport';
import ribbonImg from './assets/ribbon.png';

const WITH_MESSAGE_STACK_BREAKPOINT = 1200;

export interface CourseCompletionViewProps {
  courseTitle: string;
  completedMessage?: string;
  certificateEnabled?: boolean;
  /** User-facing message shown when a certificate download attempt failed. */
  certificateError?: string | null;
  onSaveExit?: () => void;
  onDownloadCertificate?: () => void;
  isDownloadingCertificate?: boolean;
}

const BADGE_SIZE_WITH_MESSAGE = { width: 119, height: 162 };
const BADGE_SIZE_NO_MESSAGE = { width: 84, height: 115 };

export function CourseCompletionView({
  courseTitle,
  completedMessage,
  certificateEnabled = true,
  certificateError = null,
  onSaveExit,
  onDownloadCertificate,
  isDownloadingCertificate = false,
}: CourseCompletionViewProps): React.ReactElement {
  const { tokens } = useTheme();
  const { t } = useTranslation();
  const sharedStyles = createContentBlockStyles(tokens);
  const body = completedMessage?.trim();
  const stackWithMessage = useIsMobileViewport(WITH_MESSAGE_STACK_BREAKPOINT);
  const [isSaveExitHovered, setIsSaveExitHovered] = useState(false);
  const styles = createStyles(tokens);

  const actionCard = (
    <div style={styles.actionCard}>
      <div style={styles.actionCopy}>
        <p style={styles.actionHeading}>{t('course_completion.action_heading')}</p>
        <p style={styles.actionBody}>{t('course_completion.action_body')}</p>
      </div>
      <button
        type="button"
        style={{
          ...sharedStyles.actionButton,
          ...styles.saveExitButton,
          ...(isSaveExitHovered ? styles.saveExitButtonHover : {}),
        }}
        onClick={onSaveExit}
        onMouseEnter={() => setIsSaveExitHovered(true)}
        onMouseLeave={() => setIsSaveExitHovered(false)}
        data-testid="course-completion-save-exit"
      >
        {t('course_completion.save_exit')}
      </button>
    </div>
  );

  const certificateLink = certificateEnabled ? (
    <>
      <button
        type="button"
        style={{
          ...styles.certificateLink,
          ...(isDownloadingCertificate ? styles.certificateLinkDisabled : {}),
        }}
        onClick={() => {
          onDownloadCertificate?.();
        }}
        disabled={isDownloadingCertificate}
        aria-busy={isDownloadingCertificate}
        data-testid="course-completion-download-cert"
      >
        <span style={styles.certificateLinkLastChunk}>
          <span style={styles.certificateLinkText}>
            {isDownloadingCertificate
              ? t('course_completion.downloading_certificate')
              : t('course_completion.download_certificate')}
          </span>
          {isDownloadingCertificate ? null : <DownloadIcon />}
        </span>
      </button>
      {certificateError ? (
        <span role="alert" style={styles.certificateError} data-testid="course-completion-cert-error">
          {certificateError}
        </span>
      ) : null}
    </>
  ) : null;

  return (
    <section
      style={styles.container}
      data-testid="course-completion-view"
      aria-label={t('course_completion.aria_label', { courseTitle: courseTitle || 'Untitled Course' })}
    >
      {body ? (
        stackWithMessage ? (
          <div style={styles.stageWithMessageStacked}>
            <div style={styles.topRowStacked}>
              <img
                src={ribbonImg}
                alt={t('course_completion.badge_alt')}
                width={BADGE_SIZE_WITH_MESSAGE.width}
                height={BADGE_SIZE_WITH_MESSAGE.height}
                style={styles.badgeImage}
              />
              <div style={styles.contentColumn}>
                <div style={styles.contentColumnInner}>
                  <h1 style={styles.headlineLeft}>{t('course_completion.title')}</h1>
                  <p style={styles.description}>{body}</p>
                </div>
              </div>
            </div>
            <div style={styles.actionCardWrapStacked}>{actionCard}</div>
            {certificateLink ? <div style={styles.certificateLinkStackedWrap}>{certificateLink}</div> : null}
          </div>
        ) : (
          <div style={styles.stageWithMessage}>
            <img
              src={ribbonImg}
              alt={t('course_completion.badge_alt')}
              width={BADGE_SIZE_WITH_MESSAGE.width}
              height={BADGE_SIZE_WITH_MESSAGE.height}
              style={styles.badgeImage}
            />
            <div style={styles.contentColumn}>
              <div style={styles.contentColumnInner}>
                <h1 style={styles.headlineLeft}>{t('course_completion.title')}</h1>
                <p style={styles.description}>{body}</p>
                {certificateLink}
              </div>
            </div>
            <div style={styles.actionCardWrapWithMessage}>{actionCard}</div>
          </div>
        )
      ) : (
        <div style={styles.stageNoMessage}>
          <div style={styles.heroNoMessage}>
            <img
              src={ribbonImg}
              alt={t('course_completion.badge_alt')}
              width={BADGE_SIZE_NO_MESSAGE.width}
              height={BADGE_SIZE_NO_MESSAGE.height}
              style={styles.badgeImage}
            />
            <h1 style={styles.headlineCentered}>{t('course_completion.title')}</h1>
          </div>
          <div style={styles.actionCardWrapNoMessage}>{actionCard}</div>
          {certificateLink}
        </div>
      )}
    </section>
  );
}

CourseCompletionView.displayName = 'CourseCompletionView';

export function DownloadIcon(): React.ReactElement {
  return (
    <svg
      width={16}
      height={16}
      viewBox="0 0 16 16"
      aria-hidden="true"
      style={{ verticalAlign: 'middle', marginLeft: '8px' }}
    >
      <path
        d="M 7.787 0 C 7.611 0 7.441 0.07 7.316 0.195 C 7.191 0.32 7.121 0.49 7.121 0.667 L 7.121 9.167 L 4.487 6.5 C 4.362 6.376 4.194 6.306 4.017 6.306 C 3.841 6.306 3.672 6.376 3.547 6.5 C 3.48 6.562 3.427 6.637 3.39 6.72 C 3.353 6.804 3.334 6.894 3.333 6.985 C 3.332 7.076 3.351 7.167 3.386 7.251 C 3.422 7.335 3.475 7.411 3.541 7.473 L 7.254 11.187 C 7.378 11.31 7.546 11.379 7.721 11.38 C 7.808 11.381 7.895 11.364 7.977 11.331 C 8.058 11.297 8.132 11.248 8.194 11.187 L 8.241 11.133 L 11.894 7.48 C 11.957 7.418 12.006 7.344 12.04 7.263 C 12.074 7.182 12.091 7.095 12.091 7.007 C 12.091 6.919 12.074 6.832 12.04 6.75 C 12.006 6.669 11.957 6.595 11.894 6.533 C 11.769 6.409 11.6 6.339 11.424 6.339 C 11.248 6.339 11.079 6.409 10.954 6.533 L 8.454 9.047 L 8.454 0.667 C 8.454 0.49 8.384 0.32 8.259 0.195 C 8.134 0.07 7.964 0 7.787 0 Z"
        fill="currentColor"
      />
      <path
        d="M 0 14 C 0 14.53 0.211 15.039 0.586 15.414 C 0.961 15.789 1.47 16 2 16 L 14 16 C 14.53 16 15.039 15.789 15.414 15.414 C 15.789 15.039 16 14.53 16 14 L 16 10.667 C 16 10.49 15.93 10.32 15.805 10.195 C 15.68 10.07 15.51 10 15.333 10 C 15.157 10 14.987 10.07 14.862 10.195 C 14.737 10.32 14.667 10.49 14.667 10.667 L 14.667 14 C 14.667 14.177 14.596 14.346 14.471 14.471 C 14.346 14.596 14.177 14.667 14 14.667 L 2 14.667 C 1.823 14.667 1.654 14.596 1.529 14.471 C 1.404 14.346 1.333 14.177 1.333 14 L 1.333 10.667 C 1.333 10.49 1.263 10.32 1.138 10.195 C 1.013 10.07 0.843 10 0.667 10 C 0.49 10 0.32 10.07 0.195 10.195 C 0.07 10.32 0 10.49 0 10.667 L 0 14 Z"
        fill="currentColor"
      />
    </svg>
  );
}

function createStyles(tokens: ThemeTokens): Record<string, React.CSSProperties> {
  const cardStyles: React.CSSProperties = {
    boxSizing: 'border-box',
    backgroundColor: tokens.backgrounds.lessonSectionTinted,
    borderRadius: tokens.radius.xs,
    padding: `${tokens.spacing.xl} ${tokens.spacing["2xl"]}`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: tokens.spacing.lgPlus,
  };

  return {
    container: {
      flex: 1,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      backgroundColor: tokens.backgrounds.app,
      boxSizing: 'border-box',
      fontFamily: tokens.typography.fontFamily,
      paddingTop: tokens.spacing["7xl"],
      paddingBottom: tokens.spacing["7xl"],
      paddingLeft: tokens.spacing["6xl"],
      paddingRight: tokens.spacing["6xl"],
    },

    // No-message: centered single column
    stageNoMessage: {
      width: '100%',
      maxWidth: '828px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: tokens.spacing.lgPlus,
      boxSizing: 'border-box',
    },
    heroNoMessage: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: tokens.spacing.sm,
    },
    headlineCentered: {
      margin: 0,
      textAlign: 'center',
      fontSize: tokens.typography.h2.size,
      fontWeight: tokens.typography.h2.weight,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
    },
    actionCardWrapNoMessage: {
      width: '100%',
      maxWidth: '542px',
      display: 'flex',
    },

    // With-message: three columns (badge | content | action card)
    stageWithMessage: {
      width: '100%',
      maxWidth: '1108px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.lgPlus,
      boxSizing: 'border-box',
    },
    contentColumn: {
      flex: '4 1 0',
      minWidth: 0,
      display: 'flex',
    },
    contentColumnInner: {
      width: '100%',
      paddingRight: tokens.spacing.lg,
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: tokens.spacing.sm,
    },
    headlineLeft: {
      margin: 0,
      fontSize: tokens.typography.h2.size,
      fontWeight: tokens.typography.h2.weight,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
    },
    description: {
      margin: 0,
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.body.weight,
      lineHeight: tokens.typography.body.lineHeight,
      color: tokens.text.primary,
    },
    actionCardWrapWithMessage: {
      flex: '3 1 0',
      minWidth: 0,
      display: 'flex',
    },

    // With-message stacked (below 1200px): top row (badge + content), then action card below
    stageWithMessageStacked: {
      width: '100%',
      maxWidth: '1108px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: tokens.spacing["2xl"],
      boxSizing: 'border-box',
    },
    topRowStacked: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: tokens.spacing.lgPlus,
    },
    actionCardWrapStacked: {
      width: '100%',
      display: 'flex',
    },
    certificateLinkStackedWrap: {
      alignSelf: 'center',
    },

    // Action card (shared between variants)
    actionCard: {
      ...cardStyles,
      width: '100%',
    },
    actionCopy: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: tokens.spacing.lg,
      textAlign: 'center',
      color: tokens.text.primary,
    },
    actionHeading: {
      margin: 0,
      fontSize: tokens.typography.sizes.lg,
      fontWeight: tokens.typography.weights.bold,
      lineHeight: tokens.typography.lineHeights.tight,
      color: tokens.text.primary,
    },
    actionBody: {
      margin: 0,
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.body.weight,
      lineHeight: tokens.typography.body.lineHeight,
      color: tokens.text.primary,
    },
    inlineBold: {
      fontWeight: tokens.typography.weights.bold,
    },
    saveExitButton: {
      flex: 'none',
      // NA-242: Save & Exit keeps its historical fixed blue — never the
      // account accent.
      backgroundColor: tokens.ui.fixedAccent,
      color: tokens.text.onBrand,
      transition: `background-color ${tokens.ui.transitionDefault}`,
    },
    saveExitButtonHover: {
      backgroundColor: tokens.ui.fixedAccentHover,
    },

    badgeImage: {
      display: 'block',
      flex: 'none',
    },

    certificateLink: {
      display: 'inline-block',
      textAlign: 'left',
      background: 'transparent',
      border: 'none',
      padding: 0,
      fontFamily: tokens.typography.fontFamily,
      fontSize: tokens.typography.body.size,
      fontWeight: tokens.typography.body.weight,
      lineHeight: tokens.typography.body.lineHeight,
      color: tokens.text.primary,
      cursor: 'pointer',
    },
    certificateLinkDisabled: {
      cursor: 'default',
      opacity: 0.5,
    },
    certificateLinkText: {
      textDecoration: 'underline',
      textDecorationThickness: '1px',
      textUnderlineOffset: '3px',
    },
    certificateLinkLastChunk: {
      whiteSpace: 'nowrap',
    },
    certificateError: {
      display: 'block',
      marginTop: tokens.spacing.xs,
      fontSize: tokens.typography.sizes.base,
      color: tokens.semantic.error,
    },
  };
}
